import {
  useLoaderData,
  useParams,
  useNavigate,
  useRouteLoaderData,
  useBlocker,
  useSubmit,
} from 'react-router-dom'
import { styled } from '@mui/material/styles'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import CloudDoneIcon from '@mui/icons-material/CloudDone'
import SaveIcon from '@mui/icons-material/Save'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'react-toastify'

import { LoaderData, AbstractStatus } from '../../../../types'
import { createUniqueDeepClone } from '../../../../utils'
import { abstractLoader, abstractSubmitListEventsLoader } from '../../Abstracts.loader'
import { AbstractSchema } from '../../Abstracts.schema'
import AbstractForm from '../../components/AbstractForm'
import { defaultAbstract } from '../../Abstracts.utils'

const Form = styled('form')(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 0',
  flexBasis: 'inherit',
}))

export default function AbstractEdit() {
  const { t } = useTranslation('abstracts')
  const { eventId, abstractId } = useParams()
  const navigate = useNavigate()
  const submit = useSubmit()

  const abstract = useLoaderData() as LoaderData<ReturnType<typeof abstractLoader>>
  const events = useRouteLoaderData('submit-abstracts-list') as LoaderData<
    ReturnType<typeof abstractSubmitListEventsLoader>
  >

  const methods = useForm<AbstractSchema>({
    defaultValues: abstract ?? createUniqueDeepClone(defaultAbstract(Number(eventId))),
    resolver: zodResolver(AbstractSchema),
  })
  const { getValues, handleSubmit, watch } = methods

  const status = watch('status')

  useBlocker(({ nextLocation }) => {
    if (status !== AbstractStatus.Draft) return false
    if (
      !nextLocation.pathname.includes(`/abstracts/list/${eventId}/abstract/${abstractId}/submit`)
    ) {
      return !window.confirm(
        `${t(
          'Êtes-vous sûr de vouloir quitter cette page ? Des modifications non enregistrées seront perdues'
        )}.`
      )
    }
    return false
  })

  const onSubmitForm = (
    event: React.FormEvent<HTMLFormElement | HTMLButtonElement>,
    shouldSubmit = false
  ) => {
    event.preventDefault()
    const values = getValues()

    try {
      const data = AbstractSchema.safeParse(values)

      if ('error' in data && !data.success) {
        toast.error(t("Le schéma de donnée n'est pas valide", { ns: 'common' }))
        data.error.issues.forEach(issue => {
          toast.warn(`${issue.path.toString()}: ${issue.message}`, {
            autoClose: 60000,
          })
        })
        console.error(data.error)
      }
      if ('data' in data && data.success) {
        submit(data.data, {
          method: 'POST',
          encType: 'application/json',
          action: `/abstracts/list/${eventId}/abstract/${abstractId}/submit?shouldSubmit=${shouldSubmit}`,
        })
      }
    } catch (e) {
      console.error(e)
      toast.error(t("Le schéma de donnée n'est pas valide", { ns: 'common' }))
      toast.warn(t('Erreur innatendue', { ns: 'common' }))
    }
  }

  const event = useMemo(
    () => events?.find(event => event.id === Number(eventId)),
    [eventId, events]
  )

  return (
    <FormProvider {...methods}>
      <Form onSubmit={onSubmitForm}>
        <Stack flex="1 0" margin="1rem" sx={{ overflowY: 'auto' }}>
          <Stack flex="1 0 auto" gap="1rem">
            <Stack direction="row" justifyContent="space-between" alignContent="center">
              <Typography variant="h2">
                {abstractId === 'new' ? t("Ajout d'un résumé") : t("Édition d'un résumé")}
              </Typography>
              <Typography variant="h3">{event?.name}</Typography>
            </Stack>
            <Divider sx={{ margin: 0 }} />
            <Stack direction="column" flex="1 0 auto" gap="1rem">
              <Stack direction="column" flex="1 0 auto">
                <AbstractForm readOnly={status !== AbstractStatus.Draft} />
              </Stack>

              <Stack direction="row" justifyContent={'space-between'} gap="1rem">
                <Button
                  variant="outlined"
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()

                    navigate(`/abstracts/list/${eventId}`)
                  }}
                >
                  {status !== AbstractStatus.Draft ? t('Quitter') : t('Annuler')}
                </Button>
                <Stack direction="row" gap="1rem">
                  <Button
                    onClick={e => {
                      void handleSubmit(
                        () => {
                          onSubmitForm(e)
                        },
                        () => {
                          toast.error(t('Veuillez corriger les erreurs'))
                        }
                      )()
                    }}
                    disabled={status && status !== AbstractStatus.Draft}
                    color="primary"
                    startIcon={<SaveIcon />}
                  >
                    {status && status === AbstractStatus.Draft
                      ? t('Sauvegarder brouillon')
                      : t('Sauvegarder')}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={e => {
                      void handleSubmit(
                        () => {
                          if (
                            window.confirm(
                              t(
                                'Êtes-vous sûr de vouloir soumettre ce résumé ? Vous ne pourrez plus le modifier'
                              )
                            )
                          ) {
                            onSubmitForm(e, true)
                          }
                        },
                        () => {
                          toast.error(t('Veuillez corriger les erreurs'))
                        }
                      )()
                    }}
                    disabled={status && status !== AbstractStatus.Draft}
                    color="primary"
                    startIcon={
                      status && status !== AbstractStatus.Draft ? (
                        <CloudDoneIcon />
                      ) : (
                        <UploadFileIcon />
                      )
                    }
                  >
                    {status && status !== AbstractStatus.Draft ? t('Soumis') : t('Soumettre')}
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Form>
    </FormProvider>
  )
}
